// Schema Designer — pure CREATE-DDL generator (forward-only: model → CREATE
// TABLE). No DOM, node-testable (same ethos as qbsql.js). We deliberately do
// NOT diff against or ALTER an existing populated schema here — that's the part
// that can't be done correctly without rename-detection guesswork. This emits
// CREATE statements for new objects only; the preview is copy/run/open-in-tab.
//
// MODEL:
//   {
//     tables: [{
//       name,
//       columns: [{ name, type, nullable, autoInc, primaryKey, default }],
//       foreignKeys: [{ columns:[name], refTable, refColumns:[name], onDelete, onUpdate }],
//     }],
//     engine?:  'InnoDB',   // default InnoDB
//     charset?: 'utf8mb4',  // default utf8mb4
//     ifNotExists?: bool,
//   }

export const COMMON_TYPES = [
  'INT', 'BIGINT', 'TINYINT', 'SMALLINT', 'DECIMAL(10,2)', 'FLOAT', 'DOUBLE',
  'VARCHAR(255)', 'CHAR(36)', 'TEXT', 'LONGTEXT', 'DATE', 'DATETIME', 'TIMESTAMP',
  'TIME', 'BOOLEAN', 'JSON', 'BLOB', 'ENUM(\'a\',\'b\')',
];
export const FK_ACTIONS = ['', 'CASCADE', 'SET NULL', 'RESTRICT', 'NO ACTION'];

const qid = (s) => '`' + String(s ?? '').replace(/`/g, '``') + '`';

// String-typed columns (CHAR/VARCHAR/TEXT/ENUM/SET/BINARY/BLOB families).
const STRING_TYPE = /^\s*(?:n?(?:var)?char|(?:tiny|medium|long)?text|enum|set|(?:var)?binary|(?:tiny|medium|long)?blob)\b/i;

// Render a column DEFAULT. Numbers, NULL, booleans, and CURRENT_TIMESTAMP pass
// through raw; everything else is a quoted string literal (backslash escaped
// too — it's MySQL's string escape). A string-typed column quotes anything but
// NULL, so '007' stays '007' instead of becoming the number 7.
const renderDefault = (v, type) => {
  const s = String(v).trim();
  if (s === '') return null;
  const str = STRING_TYPE.test(type || '');
  if (/^null$/i.test(s)) return 'NULL';
  if (!str && /^-?\d+(\.\d+)?$/.test(s)) return s;
  if (!str && /^(true|false)$/i.test(s)) return s.toUpperCase();
  if (!str && /^current_timestamp(\(\))?$/i.test(s)) return 'CURRENT_TIMESTAMP';
  return "'" + s.replace(/\\/g, '\\\\').replace(/'/g, "''") + "'";
};

const colDef = (c) => {
  let s = `${qid(c.name)} ${c.type || 'INT'}`;
  if (!c.nullable) s += ' NOT NULL';
  if (c.autoInc) s += ' AUTO_INCREMENT';
  const def = c.default != null ? renderDefault(c.default, c.type) : null;
  if (def != null) s += ' DEFAULT ' + def;
  return s;
};

// Topologically order tables so a table is created after the tables its FKs
// reference. Falls back to input order on a cycle (the FK-checks-off wrapper
// keeps even a cyclic set runnable).
function orderTables(tables) {
  const names = new Set(tables.map((t) => t.name));
  const byName = new Map(tables.map((t) => [t.name, t]));
  const out = [];
  const done = new Set();
  const visiting = new Set();
  const visit = (t) => {
    if (done.has(t.name) || visiting.has(t.name)) return;
    visiting.add(t.name);
    for (const fk of t.foreignKeys || []) {
      if (fk.refTable && fk.refTable !== t.name && names.has(fk.refTable)) {
        const dep = byName.get(fk.refTable);
        if (dep) visit(dep);
      }
    }
    visiting.delete(t.name);
    done.add(t.name);
    out.push(t);
  };
  for (const t of tables) visit(t);
  return out;
}

export function buildCreateDDL(model) {
  const warnings = [];
  const tables = (model?.tables || []).filter((t) => t.name && t.name.trim());
  if (tables.length === 0) return { sql: '', warnings: ['Add a table to start.'] };

  const engine = model.engine || 'InnoDB';
  const charset = model.charset || 'utf8mb4';
  const ine = model.ifNotExists ? 'IF NOT EXISTS ' : '';

  // validations (don't block generation — surface as warnings)
  const allNames = new Set(tables.map((t) => t.name.toLowerCase()));
  for (const t of tables) {
    const cols = (t.columns || []).filter((c) => c.name && c.name.trim());
    if (cols.length === 0) warnings.push(`Table "${t.name}" has no columns.`);
    const seen = new Set();
    for (const c of cols) {
      const k = c.name.toLowerCase();
      if (seen.has(k)) warnings.push(`Table "${t.name}" has a duplicate column "${c.name}".`);
      seen.add(k);
    }
    const pks = cols.filter((c) => c.primaryKey);
    if (cols.some((c) => c.autoInc) && !pks.some((c) => c.autoInc)) {
      warnings.push(`Table "${t.name}": AUTO_INCREMENT column should be (part of) the primary key.`);
    }
    for (const fk of t.foreignKeys || []) {
      if (!fk.refTable) { warnings.push(`Table "${t.name}" has a foreign key with no referenced table.`); continue; }
      if (!allNames.has(fk.refTable.toLowerCase())) warnings.push(`Table "${t.name}" references "${fk.refTable}", which isn't in this design.`);
      if ((fk.columns || []).length !== (fk.refColumns || []).length || (fk.columns || []).length === 0) {
        warnings.push(`Table "${t.name}": a foreign key's column count doesn't match the referenced columns.`);
      }
    }
  }

  const ddlFor = (t) => {
    const cols = (t.columns || []).filter((c) => c.name && c.name.trim());
    const lines = cols.map(colDef);
    const pk = cols.filter((c) => c.primaryKey).map((c) => qid(c.name));
    if (pk.length) lines.push(`PRIMARY KEY (${pk.join(', ')})`);
    for (const fk of (t.foreignKeys || [])) {
      const fcols = (fk.columns || []).filter(Boolean);
      const rcols = (fk.refColumns || []).filter(Boolean);
      if (!fk.refTable || fcols.length === 0 || fcols.length !== rcols.length) continue;
      let line = `FOREIGN KEY (${fcols.map(qid).join(', ')}) REFERENCES ${qid(fk.refTable)} (${rcols.map(qid).join(', ')})`;
      if (fk.onDelete) line += ` ON DELETE ${fk.onDelete}`;
      if (fk.onUpdate) line += ` ON UPDATE ${fk.onUpdate}`;
      lines.push(line);
    }
    const body = lines.map((l) => '  ' + l).join(',\n');
    return `CREATE TABLE ${ine}${qid(t.name)} (\n${body}\n) ENGINE=${engine} DEFAULT CHARSET=${charset};`;
  };

  const ordered = orderTables(tables);
  const hasFKs = tables.some((t) => (t.foreignKeys || []).some((f) => f.refTable));
  const parts = [];
  if (hasFKs) parts.push('SET FOREIGN_KEY_CHECKS=0;\n');
  parts.push(ordered.map(ddlFor).join('\n\n'));
  if (hasFKs) parts.push('\nSET FOREIGN_KEY_CHECKS=1;');
  return { sql: parts.join('\n'), warnings };
}
